import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

const ToastContext = createContext(null);

let idSeq = 0;

const COLORS = {
  success: { bg: '#065f46', bar: '#10b981', icon: '✓' },
  error:   { bg: '#7f1d1d', bar: '#ef4444', icon: '!' },
  info:    { bg: '#0f172a', bar: '#082189', icon: 'i' },
};

const ccToastIn = keyframes`
  from{opacity:0;transform:translateX(40px)}
  to{opacity:1;transform:translateX(0)}
`;

const ToastStack = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 360px;
`;

const ToastItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ $bg }) => $bg};
  color: #fff;
  padding: 13px 16px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  border-left: 4px solid ${({ $bar }) => $bar};
  cursor: pointer;
  animation: ${ccToastIn} 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.4;
`;

const ToastIcon = styled.span`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $bar }) => $bar};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
`;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
  }, []);

  const push = useCallback((type, message, duration = 3500) => {
    const id = ++idSeq;
    setToasts((t) => [...t, { id, type, message }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const toast = {
    success: (m, d) => push('success', m, d),
    error:   (m, d) => push('error', m, d),
    info:    (m, d) => push('info', m, d),
  };

  return (
    
    <ToastContext.Provider value={toast}>
      {children}
      <ToastStack>
        {toasts.map((t) => {
          const c = COLORS[t.type] || COLORS.info;
          return (
            <ToastItem key={t.id} onClick={() => dismiss(t.id)} $bg={c.bg} $bar={c.bar}>
              <ToastIcon $bar={c.bar}>{c.icon}</ToastIcon>
              <span>{t.message}</span>
            </ToastItem>
          );
        })}
      </ToastStack>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  // Safe no-op fallback if used outside provider (keeps components resilient).
  return ctx || { success: () => {}, error: () => {}, info: () => {} };
};
